import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'

import { getMemberAccountAttributes, type AdminMemberAccount } from './api'

interface AdminMemberAttributesDrawerProps {
  member: AdminMemberAccount
  onOpenChange: (open: boolean) => void
  open: boolean
}

function sortJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJson)
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, sortJson(item)]),
    )
  }
  return value
}

export function AdminMemberAttributesDrawer({
  member,
  onOpenChange,
  open,
}: AdminMemberAttributesDrawerProps) {
  const { t } = useTranslation('adminOrganization')
  const [copied, setCopied] = useState(false)
  const attributesQuery = useQuery({
    enabled: open,
    queryFn: () => getMemberAccountAttributes(member.id),
    queryKey: ['admin-member-account-attributes', member.id],
  })
  const attributes = attributesQuery.data?.attributes
  const fieldCount = Object.keys(attributes ?? {}).length
  const prettyJson = useMemo(
    () => JSON.stringify(sortJson(attributes ?? {}), null, 2),
    [attributes],
  )

  async function copyJson() {
    try {
      await navigator.clipboard.writeText(prettyJson)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Drawer
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={t('memberAccounts.attributesDrawer.title', { name: member.name })}
    >
      <p className="text-sm leading-6 text-[var(--color-text-muted)]">
        {t('memberAccounts.attributesDrawer.description')}
      </p>

      {attributesQuery.isLoading ? (
        <div className="mt-5 rounded-lg bg-[var(--color-surface-muted)] px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('memberAccounts.attributesDrawer.loading')}
        </div>
      ) : attributesQuery.isError ? (
        <div className="mt-5">
          <ErrorState
            action={
              <Button
                className="mt-3"
                onClick={() => void attributesQuery.refetch()}
                size="sm"
                variant="secondary"
              >
                {t('actions.retry')}
              </Button>
            }
            description={t('memberAccounts.attributesDrawer.errorDescription')}
            title={t('memberAccounts.attributesDrawer.errorTitle')}
          />
        </div>
      ) : fieldCount === 0 ? (
        <div className="mt-5 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('memberAccounts.attributesDrawer.empty')}
        </div>
      ) : (
        <section className="mt-5" aria-label={t('memberAccounts.attributesDrawer.rawJson')}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-[var(--color-text-strong)]">
                {t('memberAccounts.attributesDrawer.rawJson')}
              </h3>
              <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                {t('memberAccounts.attributesDrawer.fieldCount', { count: fieldCount })}
              </p>
            </div>
            <Button onClick={() => void copyJson()} size="sm" variant="secondary">
              {copied
                ? t('memberAccounts.attributesDrawer.copied')
                : t('memberAccounts.attributesDrawer.copy')}
            </Button>
          </div>
          <pre
            aria-label={t('memberAccounts.attributesDrawer.formattedJson')}
            className="max-h-[calc(100vh-240px)] overflow-auto rounded-lg border border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] p-4 font-mono text-xs leading-5 text-[var(--color-text)]"
            tabIndex={0}
          >
            {prettyJson}
          </pre>
        </section>
      )}
    </Drawer>
  )
}
